import { Audit, Purchase, PurchaseItem } from "types";
import { applyAuditGrouping } from "./auditGroupingHelper";
import { OrderFulfilledTimelineItem, OrderPartiallyFulfilledTimelineItem } from "./Items/OrderFulfillmentTimelineItem";
import { getAuditData } from "@/models/Audit";

const FULFILLMENT_AUDIT_GROUPING_THRESHOLD_SECONDS = 10;

const hasFulfillmentChange = (purchase: Purchase, item: PurchaseItem | undefined, audit: Audit): boolean => {
    if (!item) {
        const items = Array.isArray(audit.data?.items) ? audit.data?.items : Object.values(audit.data?.items ?? {});
        return items.some((currentItem: any) => !!currentItem.fulfillmentStatus || !!currentItem.fulfillmentUnits);
    }

    return (
        OrderFulfilledTimelineItem.supportsAudit(purchase, item, audit) ||
        OrderPartiallyFulfilledTimelineItem.supportsAudit(purchase, item, audit)
    );
};

const isFulfillmentAudit = (change: any, purchase: Purchase, item?: PurchaseItem): boolean => {
    if (!change.data) {
        return false;
    }

    const audit = getAuditData(change);
    return hasFulfillmentChange(purchase, item, audit as Audit);
};

const getUnitsById = (fulfillmentUnits: Record<string, any> = {}) => {
    const unitsById: Record<string, any> = {};

    for (const [key, unit] of Object.entries(fulfillmentUnits)) {
        const unitId = (unit as any)?.id ?? (unit as any)?.new?.id ?? key;
        unitsById[unitId] = unit;
    }

    return unitsById;
};

const getLineItemsById = (lineItems: Record<string, any> = {}) => {
    const itemsById: Record<string, any> = {};

    for (const [key, lineItem] of Object.entries(lineItems)) {
        const lineItemId = (lineItem as any)?.id ?? key;
        itemsById[lineItemId] = lineItem;
    }

    return itemsById;
};

const mergeFulfillmentAudits = (base: any, next: any): any => {
    const baseItems: any[] = Array.isArray(base?.data?.items)
        ? base.data.items
        : Object.values(base?.data?.items ?? {});
    const nextItems: any[] = Array.isArray(next?.data?.items)
        ? next.data.items
        : Object.values(next?.data?.items ?? {});

    const itemsById: Record<string, any> = {};

    for (const currentItem of baseItems) {
        itemsById[currentItem.id] = { ...currentItem };
    }

    for (const currentItem of nextItems) {
        const existingItem = itemsById[currentItem.id];

        if (!existingItem) {
            itemsById[currentItem.id] = { ...currentItem };
            continue;
        }

        const mergedFulfillmentUnits = {
            ...getUnitsById(existingItem.fulfillmentUnits),
            ...getUnitsById(currentItem.fulfillmentUnits),
        };

        const mergedLineItems = {
            ...getLineItemsById(existingItem.lineItems),
            ...getLineItemsById(currentItem.lineItems),
        };

        // Keep primary status from base (latest/first audit), but still merge unit/line deltas from following audits.
        itemsById[currentItem.id] = {
            ...existingItem,
            ...currentItem,
            fulfillmentStatus: existingItem.fulfillmentStatus ?? currentItem.fulfillmentStatus,
            fulfillmentUnits: mergedFulfillmentUnits,
            lineItems: mergedLineItems,
        };
    }

    return {
        ...base,
        data: {
            ...base.data,
            items: itemsById,
        },
    };
};

export const applyFulfillmentAuditGrouping = (sortedList: any[], purchase: Purchase, item?: PurchaseItem): any[] => {
    return applyAuditGrouping(sortedList, {
        thresholdSeconds: FULFILLMENT_AUDIT_GROUPING_THRESHOLD_SECONDS,
        isGroupable: (change) => isFulfillmentAudit(change, purchase, item),
        merge: mergeFulfillmentAudits,
    });
};
